import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, FileText, Building2, Phone, Mail, Printer, ArrowRight } from 'lucide-react';
import { ContactButtons } from './ContactButtons';
import { useLanguage, Language } from '../contexts/LanguageContext';
type Tier = {
  name: string;
  who: string;
  forms: string;
  price: string;
  save: string | null;
  extra: string | null;
  popular: boolean;
};
type PricingStrings = {
  heading: string;
  headingSub: string;
  talkTitle: string;
  talkBody: string;
  assistedTitle: string;
  assistedSub: string;
  tapHint: string;
  tiers: Tier[];
  includesTitle: string;
  includes: string[];
  getStarted: string;
  selfTitle: string;
  selfBody: string;
  selfCta: string;
  photoTitle: string;
  photoBody: string;
  photoCta: string;
  uspsTitle: string;
  uspsBody: string;
  uspsCta: string;
  uspsNote: string;
  transTitle: string;
  transBody: string;
  transCta: string;
  transNote: string;
  deliveryTitle: string;
  deliveryEmailTitle: string;
  deliveryEmailBody: string;
  deliveryPrintTitle: string;
  deliveryPrintBody: string;
  twoCostTitle: string;
  twoCostSub: string;
  noorFeeTitle: string;
  noorFeeDesc: string;
  govFeeTitle: string;
  govFeeDesc: string;
  govNoteBefore: string;
  govLink: string;
  govNoteAfter: string;
};
// EN/ES finalized. HT/VI = best effort, pending native review.
const T: Record<Language, PricingStrings> = {
  en: {
    heading: 'Simple, transparent pricing',
    headingSub: 'One flat price for any form. You only pay Noor for the help — the U.S. government fee is always paid separately, directly to the government.',
    talkTitle: "Prefer to talk? We'll fill it out with you.",
    talkBody: "Call or message us on WhatsApp and we'll guide you through your form step by step, in your language. Asking which form you need is always free.",
    assistedTitle: 'With help',
    assistedSub: 'We guide you, a specialist reviews it, and it arrives printed at your door.',
    tapHint: 'Tap a package to choose it.',
    tiers: [{
      name: 'Just me',
      who: 'For one person',
      forms: '1 person',
      price: '99',
      save: null,
      extra: null,
      popular: true
    }, {
      name: 'The two of us',
      who: 'For a couple',
      forms: '2 people',
      price: '149',
      save: 'Save $49',
      extra: null,
      popular: false
    }, {
      name: 'My family',
      who: 'For a family',
      forms: 'Up to 4 people',
      price: '199',
      save: 'About $50 per person',
      extra: '+$40 per extra person',
      popular: false
    }],
    includesTitle: 'Every “with help” package includes:',
    includes: ['Guided by phone, WhatsApp, or online — in your language', 'Automatic error & completeness check', 'Reviewed personally by a Noor specialist', 'Every form your case needs — at no extra cost', 'Color-printed packet mailed to your home', 'A checklist of the steps to follow, included with your form', 'The same checklist in audio, too', 'Ready in 24–48 hours', 'Works for any passport form (new, renewal, minor, lost)'],
    getStarted: 'Get started',
    selfTitle: 'Prefer to do it yourself?',
    selfBody: 'Fill out your form on your own, online, at your own pace. You get a completed PDF — no phone help, no specialist review. You can upgrade to “with help” anytime by paying only the difference.',
    selfCta: 'Start online',
    photoTitle: 'Passport photo (digital)',
    photoBody: 'Take your photo through our platform. We check it against the official rules and hand it back in digital format (2×2), ready to print or for your online renewal.',
    photoCta: 'Request my photo',
    uspsTitle: 'Already have your photo ready?',
    uspsBody: 'Next step: book your free appointment at the nearest USPS. We give you the approved photo — you pick the day.',
    uspsCta: 'Book your official appointment here',
    uspsNote: 'Noor Passport Services does not book appointments. We connect you directly with the official USPS system.',
    transTitle: 'Is your document in another language?',
    transBody: 'If your application needs a birth certificate or other document translated into English, you can get a certified translation online.',
    transCta: 'Translate my document',
    transNote: 'Noor does not translate documents; we connect you with a certified translation service. Noor may earn a commission, at no extra cost to you.',
    deliveryTitle: 'How you receive your form',
    deliveryEmailTitle: 'By email — free',
    deliveryEmailBody: 'Get your completed form as a PDF, ready to print and sign.',
    deliveryPrintTitle: 'Printed & mailed — optional',
    deliveryPrintBody: 'We print and ship it to you in color or black & white. Included free with every “with help” package (color).',
    twoCostTitle: 'Two separate costs — nothing hidden',
    twoCostSub: 'What you pay Noor, and what you pay the government.',
    noorFeeTitle: 'Noor service fee',
    noorFeeDesc: 'Optional help preparing your form',
    govFeeTitle: 'U.S. government fee',
    govFeeDesc: 'Paid per person, directly to the government',
    govNoteBefore: "Government fees are set by the government, vary by application type, and are paid per person when you submit — Noor never collects them. A family pays one Noor package plus each person's own government fee. See current rates and apply directly at ",
    govLink: 'travel.state.gov',
    govNoteAfter: '.'
  },
  es: {
    heading: 'Precios simples y transparentes',
    headingSub: 'Un solo precio fijo para cualquier formulario. A Noor le paga solo por la ayuda — la tarifa del gobierno de EE. UU. siempre se paga aparte, directamente al gobierno.',
    talkTitle: '¿Prefiere hablar? Lo llenamos con usted.',
    talkBody: 'Llámenos o escríbanos por WhatsApp y lo guiamos paso a paso con su formulario, en su idioma. Preguntar cuál formulario necesita siempre es gratis.',
    assistedTitle: 'Con ayuda',
    assistedSub: 'Lo guiamos, un especialista lo revisa, y le llega impreso a su puerta.',
    tapHint: 'Toque un paquete para elegirlo.',
    tiers: [{
      name: 'Solo yo',
      who: 'Para una persona',
      forms: '1 persona',
      price: '99',
      save: null,
      extra: null,
      popular: true
    }, {
      name: 'Los dos',
      who: 'Para una pareja',
      forms: '2 personas',
      price: '149',
      save: 'Ahorre $49',
      extra: null,
      popular: false
    }, {
      name: 'Mi familia',
      who: 'Para una familia',
      forms: 'Hasta 4 personas',
      price: '199',
      save: 'Unos $50 por persona',
      extra: '+$40 por persona adicional',
      popular: false
    }],
    includesTitle: 'Cada paquete “con ayuda” incluye:',
    includes: ['Guiado por teléfono, WhatsApp o en línea — en su idioma', 'Revisión automática de errores y de que esté completo', 'Revisado personalmente por un especialista de Noor', 'Todos los formularios que su caso necesite — sin costo adicional', 'Paquete impreso a color y enviado a su casa', 'Una lista de los pasos a seguir, incluida junto con su formulario', 'La misma lista de pasos también en audio', 'Listo en 24–48 horas', 'Sirve para cualquier formulario de pasaporte (nuevo, renovación, menor, perdido)'],
    getStarted: 'Comenzar',
    selfTitle: '¿Prefiere hacerlo usted mismo?',
    selfBody: 'Llene su formulario por su cuenta, en línea, a su ritmo. Recibe un PDF completo — sin ayuda por teléfono ni revisión del especialista. Puede pasar a “con ayuda” cuando quiera pagando solo la diferencia.',
    selfCta: 'Empezar en línea',
    photoTitle: 'Foto de pasaporte (digital)',
    photoBody: 'Tome su foto a través de nuestra plataforma. Nosotros la revisamos según las reglas oficiales y se la entregamos en formato digital (2×2), lista para imprimir o para su renovación en línea.',
    photoCta: 'Solicitar mi foto',
    uspsTitle: '¿Ya tiene su foto lista?',
    uspsBody: 'Siguiente paso: agende su cita gratis en el USPS más cercano. Nosotros le damos la foto aprobada — usted elige el día.',
    uspsCta: 'Saque su cita oficial aquí',
    uspsNote: 'Noor Passport Services no agenda citas. Lo conectamos directo con el sistema oficial de USPS.',
    transTitle: '¿Su documento está en otro idioma?',
    transBody: 'Si su trámite necesita un acta de nacimiento u otro documento traducido al inglés, puede hacer su traducción certificada en línea.',
    transCta: 'Traducir mi documento',
    transNote: 'Noor no traduce documentos; lo conectamos con un servicio de traducción certificada. Noor puede recibir una comisión, sin costo adicional para usted.',
    deliveryTitle: 'Cómo recibe su formulario',
    deliveryEmailTitle: 'Por correo — gratis',
    deliveryEmailBody: 'Reciba su formulario completo en PDF, listo para imprimir y firmar.',
    deliveryPrintTitle: 'Impreso y enviado — opcional',
    deliveryPrintBody: 'Lo imprimimos y se lo enviamos a color o en blanco y negro. Incluido gratis en cada paquete “con ayuda” (a color).',
    twoCostTitle: 'Dos costos separados — nada escondido',
    twoCostSub: 'Lo que le paga a Noor, y lo que le paga al gobierno.',
    noorFeeTitle: 'Tarifa de servicio de Noor',
    noorFeeDesc: 'Ayuda opcional para preparar su formulario',
    govFeeTitle: 'Tarifa del gobierno de EE. UU.',
    govFeeDesc: 'Se paga por persona, directamente al gobierno',
    govNoteBefore: 'Las tarifas del gobierno las fija el gobierno, varían según el tipo de solicitud y se pagan por persona al momento de entregar — Noor nunca las cobra. Una familia paga un solo paquete de Noor más la tarifa del gobierno de cada persona. Vea las tarifas actuales y solicite directamente en ',
    govLink: 'travel.state.gov',
    govNoteAfter: '.'
  },
  ht: {
    heading: 'Pri senp e klè',
    headingSub: 'Yon sèl pri fiks pou nenpòt fòm. Ou peye Noor sèlman pou èd la — frè gouvènman ameriken an toujou peye apa, dirèkteman bay gouvènman an.',
    talkTitle: 'Ou pi renmen pale? N ap ranpli li ansanm ak ou.',
    talkBody: 'Rele oswa voye mesaj ban nou sou WhatsApp epi n ap gide ou etap pa etap nan fòm ou an, nan lang ou. Mande ki fòm ou bezwen toujou gratis.',
    assistedTitle: 'Ak èd',
    assistedSub: 'Nou gide ou, yon espesyalis tcheke li, epi li rive enprime devan pòt ou.',
    tapHint: 'Tape sou yon pakè pou chwazi li.',
    tiers: [{
      name: 'Sèlman mwen',
      who: 'Pou yon moun',
      forms: '1 moun',
      price: '99',
      save: null,
      extra: null,
      popular: true
    }, {
      name: 'Nou de a',
      who: 'Pou yon koup',
      forms: '2 moun',
      price: '149',
      save: 'Ekonomize $49',
      extra: null,
      popular: false
    }, {
      name: 'Fanmi mwen',
      who: 'Pou yon fanmi',
      forms: 'Jiska 4 moun',
      price: '199',
      save: 'Anviwon $50 pa moun',
      extra: '+$40 pou chak moun anplüs',
      popular: false
    }],
    includesTitle: 'Chak pakè “ak èd” gen ladan:',
    includes: ['Gidans pa telefòn, WhatsApp, oswa sou entènèt — nan lang ou', 'Tcheke otomatik pou erè ak pou wè si li konplè', 'Yon espesyalis Noor tcheke li pèsonèlman', 'Tout fòm ka ou a bezwen — san frè anplüs', 'Pakè enprime an koutè voye lakay ou', 'Yon lis etap pou swiv, enkli ansanm ak fòm ou an', 'Menm lis etap la an odyo tou', 'Pare nan 24–48 èdtan', 'Mache pou nenpòt fòm paspò (nouvo, renouvèlman, minè, pèdi)'],
    getStarted: 'Kòmanse',
    selfTitle: 'Ou pi renmen fè li poukont ou?',
    selfBody: 'Ranpli fòm ou poukont ou, sou entènèt, nan vitès pa w. Ou jwenn yon PDF konplè — san èd pa telefòn, san revizyon espesyalis. Ou ka pase nan “ak èd” nenpòt lè lè ou peye sèlman diferans lan.',
    selfCta: 'Kòmanse sou entènèt',
    photoTitle: 'Foto paspò (dijital)',
    photoBody: 'Pran foto ou atravè platfòm nou an. Nou tcheke li selon règ ofisyèl yo epi nou remèt ou li an fòma dijital (2×2), pare pou enprime oswa pou renouvèlman ou sou entènèt.',
    photoCta: 'Mande foto mwen',
    uspsTitle: 'Ou deja gen foto ou pare?',
    uspsBody: 'Pwochen etap: pran randevou gratis ou nan biwo USPS ki pi pre a. Nou ba ou foto apwouve a — ou chwazi jou a.',
    uspsCta: 'Pran randevou ofisyèl ou isit la',
    uspsNote: 'Noor Passport Services pa pran randevou. Nou konekte ou dirèkteman ak sistèm ofisyèl USPS la.',
    transTitle: 'Èske dokiman ou an nan yon lòt lang?',
    transBody: 'Si demand ou an bezwen yon batistè oswa yon lòt dokiman tradui an anglè, ou ka fè yon tradiksyon sètifye sou entènèt.',
    transCta: 'Tradui dokiman mwen',
    transNote: 'Noor pa tradui dokiman; nou konekte ou ak yon sèvis tradiksyon sètifye. Noor ka touche yon komisyon, san okenn frè anplis pou ou.',
    deliveryTitle: 'Kijan ou resevwa fòm ou',
    deliveryEmailTitle: 'Pa imèl — gratis',
    deliveryEmailBody: 'Jwenn fòm ou konplè an PDF, pare pou enprime ak siyen.',
    deliveryPrintTitle: 'Enprime epi voye — opsyonèl',
    deliveryPrintBody: 'Nou enprime li epi voye li ba ou an koutè oswa nwa e blan. Enkli gratis ak chak pakè “ak èd” (an koutè).',
    twoCostTitle: 'De depans separe — anyen kache',
    twoCostSub: 'Sa ou peye Noor, ak sa ou peye gouvènman an.',
    noorFeeTitle: 'Frè sèvis Noor',
    noorFeeDesc: 'Èd opsyonèl pou prepare fòm ou',
    govFeeTitle: 'Frè gouvènman ameriken an',
    govFeeDesc: 'Peye pa moun, dirèkteman bay gouvènman an',
    govNoteBefore: 'Se gouvènman an ki fikse frè yo, yo varye selon tip aplikasyon an, epi yo peye pa moun lè ou soumèt — Noor pa janm kolekte yo. Yon fanmi peye yon sèl pakè Noor plis frè gouvènman an pou chak moun. Gade tarif aktyèl yo epi aplike dirèkteman sou ',
    govLink: 'travel.state.gov',
    govNoteAfter: '.'
  },
  vi: {
    heading: 'Giá đơn giản, minh bạch',
    headingSub: 'Một mức giá cố định cho mọi mẫu đơn. Quý vị chỉ trả cho Noor phần hỗ trợ — phí chính phủ Hoa Kỳ luôn được trả riêng, trực tiếp cho chính phủ.',
    talkTitle: 'Thích nói chuyện hơn? Chúng tôi sẽ điền cùng quý vị.',
    talkBody: 'Gọi hoặc nhắn tin cho chúng tôi qua WhatsApp và chúng tôi sẽ hướng dẫn quý vị từng bước với mẫu đơn, bằng ngôn ngữ của quý vị. Hỏi quý vị cần mẫu đơn nào luôn miễn phí.',
    assistedTitle: 'Có hỗ trợ',
    assistedSub: 'Chúng tôi hướng dẫn quý vị, một chuyên viên xem lại, và nó được in và gửi đến tận nhà.',
    tapHint: 'Nhấn vào một gói để chọn.',
    tiers: [{
      name: 'Chỉ mình tôi',
      who: 'Cho một người',
      forms: '1 người',
      price: '99',
      save: null,
      extra: null,
      popular: true
    }, {
      name: 'Hai chúng tôi',
      who: 'Cho một cặp',
      forms: '2 người',
      price: '149',
      save: 'Tiết kiệm $49',
      extra: null,
      popular: false
    }, {
      name: 'Gia đình tôi',
      who: 'Cho một gia đình',
      forms: 'Tối đa 4 người',
      price: '199',
      save: 'Khoảng $50 mỗi người',
      extra: '+$40 mỗi người thêm',
      popular: false
    }],
    includesTitle: 'Mỗi gói “có hỗ trợ” bao gồm:',
    includes: ['Hướng dẫn qua điện thoại, WhatsApp hoặc trực tuyến — bằng ngôn ngữ của quý vị', 'Tự động kiểm tra lỗi và tính đầy đủ', 'Được chuyên viên Noor đích thân xem lại', 'Mọi mẫu đơn trường hợp của quý vị cần — không tính thêm phí', 'Bộ hồ sơ in màu gửi đến nhà quý vị', 'Một danh sách các bước cần làm, kèm theo mẫu đơn của quý vị', 'Cùng danh sách đó ở dạng âm thanh', 'Sẵn sàng trong 24–48 giờ', 'Dùng cho mọi mẫu đơn hộ chiếu (mới, gia hạn, trẻ vị thành niên, bị mất)'],
    getStarted: 'Bắt đầu',
    selfTitle: 'Thích tự làm?',
    selfBody: 'Tự điền mẫu đơn của quý vị, trực tuyến, theo nhịp độ của mình. Quý vị nhận được một tệp PDF hoàn chỉnh — không hỗ trợ qua điện thoại, không có chuyên viên xem lại. Quý vị có thể nâng cấp lên “có hỗ trợ” bất cứ lúc nào bằng cách chỉ trả phần chênh lệch.',
    selfCta: 'Bắt đầu trực tuyến',
    photoTitle: 'Ảnh hộ chiếu (kỹ thuật số)',
    photoBody: 'Chụp ảnh của quý vị qua nền tảng của chúng tôi. Chúng tôi kiểm tra theo quy định chính thức và trả lại cho quý vị ở định dạng kỹ thuật số (2×2), sẵn sàng để in hoặc để gia hạn trực tuyến.',
    photoCta: 'Yêu cầu ảnh của tôi',
    uspsTitle: 'Đã có sẵn ảnh của quý vị?',
    uspsBody: 'Bước tiếp theo: đặt lịch hẹn miễn phí tại USPS gần nhất. Chúng tôi cung cấp ảnh đã được duyệt — quý vị chọn ngày.',
    uspsCta: 'Đặt lịch hẹn chính thức tại đây',
    uspsNote: 'Noor Passport Services không đặt lịch hẹn. Chúng tôi kết nối quý vị trực tiếp với hệ thống chính thức của USPS.',
    transTitle: 'Giấy tờ của quý vị bằng ngôn ngữ khác?',
    transBody: 'Nếu hồ sơ của quý vị cần giấy khai sinh hoặc giấy tờ khác được dịch sang tiếng Anh, quý vị có thể làm bản dịch có chứng nhận trực tuyến.',
    transCta: 'Dịch giấy tờ của tôi',
    transNote: 'Noor không dịch giấy tờ; chúng tôi kết nối quý vị với một dịch vụ dịch thuật có chứng nhận. Noor có thể nhận hoa hồng, không tính thêm phí cho quý vị.',
    deliveryTitle: 'Cách quý vị nhận mẫu đơn',
    deliveryEmailTitle: 'Qua email — miễn phí',
    deliveryEmailBody: 'Nhận mẫu đơn đã hoàn tất dưới dạng PDF, sẵn sàng để in và ký.',
    deliveryPrintTitle: 'In và gửi — tùy chọn',
    deliveryPrintBody: 'Chúng tôi in và gửi cho quý vị bằng màu hoặc đen trắng. Miễn phí kèm theo mỗi gói “có hỗ trợ” (màu).',
    twoCostTitle: 'Hai khoản chi phí riêng biệt — không có gì giấu giếm',
    twoCostSub: 'Khoản quý vị trả cho Noor, và khoản quý vị trả cho chính phủ.',
    noorFeeTitle: 'Phí dịch vụ Noor',
    noorFeeDesc: 'Hỗ trợ tùy chọn để chuẩn bị mẫu đơn của quý vị',
    govFeeTitle: 'Phí chính phủ Hoa Kỳ',
    govFeeDesc: 'Trả theo mỗi người, trực tiếp cho chính phủ',
    govNoteBefore: 'Phí chính phủ do chính phủ quy định, thay đổi theo loại đơn, và được trả theo mỗi người khi quý vị nộp — Noor không bao giờ thu các khoản này. Một gia đình trả một gói Noor cộng với phí chính phủ riêng của mỗi người. Xem mức phí hiện tại và nộp đơn trực tiếp tại ',
    govLink: 'travel.state.gov',
    govNoteAfter: '.'
  }
};
export function Pricing() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  // Track the selected package by index so it survives a language change.
  const [selected, setSelected] = useState<number>(0);
  return <section id="pricing" className="py-24 bg-white relative z-10" data-id="element-297" data-mp="5usoeofos1k">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-298" data-mp="568q72sesjlc">
        {/* Heading */}
        <div className="text-center max-w-3xl mx-auto mb-10" data-id="element-299" data-mp="a525n0r9ako5">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-textPrimary mb-4" data-id="element-300" data-mp="a4k1zj29l467">
            {t.heading}
          </h2>
          <p className="text-lg text-brand-textSecondary" data-id="element-301" data-mp="38kuzbjoiexw9">{t.headingSub}</p>
        </div>

        {/* Prefer to talk? */}
        <div className="max-w-3xl mx-auto mb-14 bg-brand-bg/50 border border-brand-border rounded-3xl p-6 sm:p-8 text-center" data-id="element-302" data-mp="3il5eh9u3ufe2">
          <div className="flex items-center justify-center gap-2 mb-2" data-id="element-303" data-mp="3ncnenvob0bxz">
            <Phone className="w-5 h-5 text-brand-accent" aria-hidden="true" data-id="element-304" data-mp="2pwhl0qz7cjpf" />
            <h3 className="font-heading font-semibold text-xl text-brand-textPrimary" data-id="element-305" data-mp="2pudjugsotysu">
              {t.talkTitle}
            </h3>
          </div>
          <p className="text-brand-textSecondary mb-5 max-w-xl mx-auto" data-id="element-306" data-mp="2naqo2y3x9id4">
            {t.talkBody}
          </p>
          <ContactButtons size="lg" showNumber className="flex flex-col items-center" data-id="element-307" data-mp="2ie09kf7rmutt" />
        </div>

        {/* "With help" tiers */}
        <div className="text-center mb-8" data-id="element-308" data-mp="28fat8qvipifo">
          <h3 className="font-heading font-bold text-2xl text-brand-textPrimary" data-id="element-309" data-mp="28glscde0q20g">
            {t.assistedTitle}
          </h3>
          <p className="text-brand-textSecondary mt-1" data-id="element-310" data-mp="1zmh9ag54luvh">{t.assistedSub}</p>
          <p className="text-sm text-brand-textSecondary mt-2" data-id="element-311" data-mp="1zk4hzcdha85r">{t.tapHint}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-312" data-mp="1ul08slqk0aff">
          {t.tiers.map((tier, index) => {
          const isSelected = selected === index;
          return <div key={index} onClick={() => setSelected(index)} role="button" tabIndex={0} aria-pressed={isSelected} aria-label={`${tier.name} — $${tier.price}`} onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setSelected(index);
            }
          }} className={`relative bg-white rounded-3xl p-7 sm:p-8 flex flex-col cursor-pointer transition-all focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2 ${isSelected ? 'border-2 border-brand-accent shadow-premium' : 'border border-brand-border shadow-sm hover:border-brand-accent/60'}`} data-id="element-313" data-mp="1koezho7i4d5s" data-mpi={index}>
                {tier.popular && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-brand-accent text-brand-textPrimary px-4 py-1 rounded-full text-xs font-bold tracking-wide uppercase" data-id="element-314" data-mp="34zk0fefzcnq2">
                    {language === 'es' ? 'Más popular' : language === 'ht' ? 'Pi popilè' : language === 'vi' ? 'Phổ biến nhất' : 'Most Popular'}
                  </div>}
                {isSelected && <div className="absolute top-4 right-4 w-7 h-7 rounded-full bg-brand-accent flex items-center justify-center shadow-sm" data-id="element-315" data-mp="1kevawguctz1i">
                    <Check className="w-4 h-4 text-brand-textPrimary" data-id="element-316" data-mp="rsu3t3r04nf5" />
                  </div>}
                <div className="text-center mb-5" data-id="element-317" data-mp="wqlslowcntf4">
                  <h4 className="text-xl font-bold text-brand-textPrimary" data-id="element-318" data-mp="wp11685c0qlh">
                    {tier.name}
                  </h4>
                  <p className="text-sm text-brand-textSecondary" data-id="element-319" data-mp="15k8org4gyyou">{tier.who}</p>
                  <p className="text-xs font-medium uppercase tracking-wide text-brand-textSecondary mt-1" data-id="element-320" data-mp="91subls999hc">
                    {tier.forms}
                  </p>
                </div>
                <div className="text-center mb-1" data-id="element-321" data-mp="d1bj4sn764pg">
                  <span className="text-2xl font-bold text-brand-textPrimary align-top" data-id="element-322" data-mp="1gda5gwa10t2x">
                    $
                  </span>
                  <span className="text-5xl font-bold text-brand-textPrimary tracking-tight" data-id="element-323" data-mp="1beyxkr3aksnc">
                    {tier.price}
                  </span>
                </div>
                <p className="text-center text-sm font-semibold text-brand-accent min-h-[20px] mb-1" data-id="element-324" data-mp="1l6d1db2a0ema">
                  {tier.save || ''}
                </p>
                <p className="text-center text-xs text-brand-textSecondary min-h-[16px] mb-6" data-id="element-325" data-mp="sgmjqs1fqulf">
                  {tier.extra || ''}
                </p>
                <Link to="/contact" onClick={e => e.stopPropagation()} className={`mt-auto w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-base transition-all focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2 ${isSelected ? 'bg-brand-accent hover:bg-brand-accentHover text-brand-textPrimary shadow-sm hover:shadow-md' : 'bg-brand-textPrimary text-white hover:opacity-90'}`} data-id="element-326" data-mp="16hp7d982wcyg">
                  {t.getStarted}
                  <ArrowRight className="w-4 h-4" data-id="element-327" data-mp="2bfaqvr9z5p6n" />
                </Link>
              </div>;
        })}
        </div>

        {/* Shared list of what every "with help" package includes */}
        <div className="max-w-3xl mx-auto bg-brand-bg/40 border border-brand-border rounded-2xl p-6 sm:p-8 mb-16" data-id="element-328" data-mp="343zr7o37hqhc">
          <p className="font-semibold text-brand-textPrimary mb-4 text-center" data-id="element-329" data-mp="3932pmpookygc">
            {t.includesTitle}
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3" data-id="element-330" data-mp="2uddlqmp7egcd">
            {t.includes.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-331" data-mp="2zb6qdbauz2aj" data-mpi={i}>
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white border border-brand-border flex items-center justify-center mt-0.5" data-id="element-332" data-mp="3n2c33hli5egm">
                  <Check className="w-4 h-4 text-brand-accent" data-id="element-333" data-mp="3rvdmo2wnm26j" />
                </div>
                <span className="text-brand-textPrimary text-sm" data-id="element-334" data-mp="246k3y7f8o53w">{item}</span>
              </li>)}
          </ul>
        </div>

        {/* Self-service */}
        <div className="max-w-3xl mx-auto bg-white border border-brand-border rounded-3xl p-7 sm:p-8 mb-8" data-id="element-335" data-mp="1u603v83gh6n9">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5" data-id="element-336" data-mp="1p8rt3skn04w1">
            <div className="max-w-md" data-id="element-337" data-mp="2mufdtg7qcyrq">
              <h3 className="font-heading font-bold text-xl text-brand-textPrimary mb-1" data-id="element-338" data-mp="2mxjpeyniywsf">
                {t.selfTitle}
              </h3>
              <p className="text-brand-textSecondary text-sm" data-id="element-339" data-mp="2d55lbz8z5e33">{t.selfBody}</p>
            </div>
            <div className="text-center flex-shrink-0" data-id="element-340" data-mp="286ufdzlsvnq5">
              <div className="mb-2" data-id="element-341" data-mp="286avidclap9h">
                <span className="text-xl font-bold text-brand-textPrimary align-top" data-id="element-342" data-mp="35loqfycss3nj">
                  $
                </span>
                <span className="text-4xl font-bold text-brand-textPrimary" data-id="element-343" data-mp="30u6yfzsr5dep">
                  39
                </span>
              </div>
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-base border border-brand-textPrimary text-brand-textPrimary hover:bg-brand-bg transition-colors" data-id="element-344" data-mp="395yz6j6lurqy">
                {t.selfCta}
              </Link>
            </div>
          </div>
        </div>

        {/* Passport photo add-on — standalone option, same shape as self-service */}
        <div className="max-w-3xl mx-auto bg-white border border-brand-border rounded-3xl p-7 sm:p-8 mb-6" data-id="element-345" data-mp="3w3aaxivrzmat">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5" data-id="element-346" data-mp="4qz32nqiccy1">
            <div className="max-w-md" data-id="element-347" data-mp="9nfr7hxpirb4">
              <h3 className="font-heading font-bold text-xl text-brand-textPrimary mb-1" data-id="element-348" data-mp="9qlhh0i2nz8f">
                {t.photoTitle}
              </h3>
              <p className="text-brand-textSecondary text-sm" data-id="element-349" data-mp="jm5btigzmpv2">{t.photoBody}</p>
            </div>
            <div className="text-center flex-shrink-0" data-id="element-350" data-mp="3q3nkl2yn7jbq">
              <div className="mb-2" data-id="element-351" data-mp="3q22v3rr6qqkq">
                <span className="text-xl font-bold text-brand-textPrimary align-top" data-id="element-352" data-mp="3l5m3gnks1u2w">
                  $
                </span>
                <span className="text-4xl font-bold text-brand-textPrimary" data-id="element-353" data-mp="3pe0v75nqzha">
                  12
                </span>
              </div>
              <a href="https://buy.stripe.com/aFaaEXgY94iH01d9IBe7m00" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-base border border-brand-textPrimary text-brand-textPrimary hover:bg-brand-bg transition-colors" data-id="element-354" data-mp="3v1fdo0b0xbhi">
                {t.photoCta}
              </a>
            </div>
          </div>
        </div>

        {/* USPS appointment — next step after the photo. Links out to the official
            USPS scheduler. Noor does NOT book appointments (made explicit). */}
        <div className="max-w-3xl mx-auto bg-brand-accent/5 border border-brand-accent/30 rounded-3xl p-7 sm:p-8 mb-6 text-center" data-id="element-355" data-mp="yyr44g1jvjk5">
          <h3 className="font-heading font-bold text-xl text-brand-textPrimary mb-2" data-id="element-356" data-mp="13vzpsqlwq5q5">
            {t.uspsTitle}
          </h3>
          <p className="text-brand-textSecondary max-w-xl mx-auto mb-6" data-id="element-357" data-mp="u6zkkpgtae3i">
            {t.uspsBody}
          </p>
          <a href="https://tools.usps.com/rcas.htm" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-brand-accent hover:bg-brand-accentHover text-brand-textPrimary px-8 py-4 rounded-full font-semibold text-base transition-all shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2" data-id="element-358" data-mp="g6otpmfi4zic">
            {t.uspsCta}
            <ArrowRight className="w-4 h-4" data-id="element-359" data-mp="20kyyu7ubfbje" />
          </a>
          <p className="text-xs text-brand-textSecondary mt-5 max-w-md mx-auto leading-relaxed" data-id="element-360" data-mp="25l3b1lo6xk3t">
            {t.uspsNote}
          </p>
        </div>

        {/* Document translation referral — EXTERNAL PARTNER (affiliate).
            Kept GENERIC on purpose (no partner named) so the partner can change
            by only swapping the link. The button points to /contact as a
            placeholder until the real affiliate link is available; when ready,
            replace the <Link to="/contact"> with an external
            <a href={AFFILIATE_URL} target="_blank" rel="noopener noreferrer">.
            Commission is disclosed in transNote (FTC). Neutral surface + navy
            button to distinguish it from the (free, official) USPS step. */}
        <div className="max-w-3xl mx-auto bg-brand-bg/50 border border-brand-border rounded-3xl p-7 sm:p-8 mb-16 text-center" data-id="element-361" data-mp="3rueyeosvjfo5">
          <h3 className="font-heading font-bold text-xl text-brand-textPrimary mb-2" data-id="element-362" data-mp="1v2er9fjbfz9g">
            {t.transTitle}
          </h3>
          <p className="text-brand-textSecondary max-w-xl mx-auto mb-6" data-id="element-363" data-mp="1q3la9d4tlq4j">
            {t.transBody}
          </p>
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-brand-textPrimary text-white hover:opacity-90 px-8 py-4 rounded-full font-semibold text-base transition-all shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2" data-id="element-364" data-mp="1xmb4tyiz3gjs">
            {t.transCta}
            <ArrowRight className="w-4 h-4" data-id="element-365" data-mp="1eoqppj60fwcd" />
          </Link>
          <p className="text-xs text-brand-textSecondary mt-5 max-w-md mx-auto leading-relaxed" data-id="element-366" data-mp="1jms9657clxod">
            {t.transNote}
          </p>
        </div>

        {/* Delivery choice */}
        <div className="max-w-3xl mx-auto mb-16" data-id="element-367" data-mp="1oo96jqqmj207">
          <p className="text-center font-semibold text-brand-textPrimary mb-4" data-id="element-368" data-mp="1sojakxuuvicf">
            {t.deliveryTitle}
          </p>
          <div className="grid sm:grid-cols-2 gap-4" data-id="element-369" data-mp="300iaj47327ub">
            <div className="bg-white border border-brand-border rounded-2xl p-5 flex items-start gap-3" data-id="element-370" data-mp="3423vzdxonug4">
              <div className="w-10 h-10 rounded-lg bg-brand-accent/10 flex items-center justify-center flex-shrink-0" data-id="element-371" data-mp="3v46gjs6h1ux5">
                <Mail className="w-5 h-5 text-brand-accent" data-id="element-372" data-mp="3wty79turbfn" />
              </div>
              <div data-id="element-373" data-mp="3lfeu1zk6k2tm">
                <p className="font-semibold text-brand-textPrimary" data-id="element-374" data-mp="3ldvds8yixrru">
                  {t.deliveryEmailTitle}
                </p>
                <p className="text-sm text-brand-textSecondary" data-id="element-375" data-mp="3qbn2ku3vgxrt">
                  {t.deliveryEmailBody}
                </p>
              </div>
            </div>
            <div className="bg-white border border-brand-border rounded-2xl p-5 flex items-start gap-3" data-id="element-376" data-mp="3he33nrthfdkd">
              <div className="w-10 h-10 rounded-lg bg-brand-accent/10 flex items-center justify-center flex-shrink-0" data-id="element-377" data-mp="32h2he3u28ata">
                <Printer className="w-5 h-5 text-brand-accent" data-id="element-378" data-mp="37f40upveec5a" />
              </div>
              <div data-id="element-379" data-mp="10oxjmbpvqjan">
                <p className="font-semibold text-brand-textPrimary" data-id="element-380" data-mp="10mbc2c2zo3sf">
                  {t.deliveryPrintTitle}
                </p>
                <p className="text-sm text-brand-textSecondary" data-id="element-381" data-mp="2q59uteqs3izy">
                  {t.deliveryPrintBody}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Two-cost breakdown */}
        <div className="max-w-2xl mx-auto bg-brand-bg/40 rounded-3xl p-6 sm:p-8 border border-brand-border" data-id="element-382" data-mp="2zweayhs6x9wd">
          <h4 className="font-heading font-semibold text-lg text-brand-textPrimary mb-1 text-center" data-id="element-383" data-mp="2v3ukwlcbgmao">
            {t.twoCostTitle}
          </h4>
          <p className="text-sm text-brand-textSecondary text-center mb-6" data-id="element-384" data-mp="38xvktif08fcm">
            {t.twoCostSub}
          </p>

          <div className="space-y-3" data-id="element-385" data-mp="3ityo4rllek15">
            <div className="flex items-start justify-between gap-4 bg-white rounded-xl p-4 border border-brand-border" data-id="element-386" data-mp="3isw1ivvaz3r1">
              <div className="flex items-start gap-3 min-w-0" data-id="element-387" data-mp="3dvx8yhraf4uy">
                <div className="w-10 h-10 rounded-lg bg-brand-accent/10 flex items-center justify-center flex-shrink-0" data-id="element-388" data-mp="1oh8so5fkt2rn">
                  <FileText className="w-5 h-5 text-brand-accent" data-id="element-389" data-mp="1jinu0afz2r5b" />
                </div>
                <div className="min-w-0" data-id="element-390" data-mp="3jknlswo3tqt4">
                  <p className="font-semibold text-brand-textPrimary" data-id="element-391" data-mp="3oifevu0hjiyw">
                    {t.noorFeeTitle}
                  </p>
                  <p className="text-sm text-brand-textSecondary" data-id="element-392" data-mp="3sjr7m8enokbb">
                    {t.noorFeeDesc}
                  </p>
                </div>
              </div>
              <p className="font-heading font-bold text-xl text-brand-textPrimary whitespace-nowrap" data-id="element-393" data-mp="1bd4gtv7xzyw">
                $39–$199
              </p>
            </div>

            <div className="flex items-start justify-between gap-4 bg-white rounded-xl p-4 border border-brand-border" data-id="element-394" data-mp="bacby948zpkb">
              <div className="flex items-start gap-3 min-w-0" data-id="element-395" data-mp="g4qauli4cetq">
                <div className="w-10 h-10 rounded-lg bg-brand-secondaryLink/10 flex items-center justify-center flex-shrink-0" data-id="element-396" data-mp="k35xgoaafl6m">
                  <Building2 className="w-5 h-5 text-brand-secondaryLink" data-id="element-397" data-mp="p54w5kv31tx8" />
                </div>
                <div className="min-w-0" data-id="element-398" data-mp="u2occqbccgrt">
                  <p className="font-semibold text-brand-textPrimary" data-id="element-399" data-mp="146uzhoce5cm8">
                    {t.govFeeTitle}
                  </p>
                  <p className="text-sm text-brand-textSecondary" data-id="element-400" data-mp="z6h1z08f1l2g">
                    {t.govFeeDesc}
                  </p>
                </div>
              </div>
              <p className="font-heading font-bold text-xl text-brand-textPrimary whitespace-nowrap" data-id="element-401" data-mp="q6u06dbryd8v">
                $130+
              </p>
            </div>
          </div>

          <p className="text-xs text-brand-textSecondary mt-6 leading-relaxed" data-id="element-402" data-mp="1mynu1n73z5w2">
            {t.govNoteBefore}
            <a href="https://travel.state.gov" target="_blank" rel="noopener noreferrer" className="text-brand-secondaryLink underline hover:text-brand-textPrimary transition-colors" data-id="element-403" data-mp="1d706hxdoa8zv">
              {t.govLink}
            </a>
            {t.govNoteAfter}
          </p>
        </div>
      </div>
    </section>;
}